import { useQueryClient } from '@tanstack/react-query'
import type { Locale } from '@quoter/shared/locale'
import { supabase } from '../lib/supabase'
import { sessionKeys, type Profile } from '../session/queries'
import { useSession } from '../session/useSession'
import { getProduct } from '../config'

type ProfileQueryData = { profile: Profile; subscriptionStatus: string | null } | null

/**
 * Persist the interface language on the caller's own membership, through the
 * set_my_locale function (which routes to profiles for the first product), so
 * any provisioned user can do it and nobody can touch another column.
 *
 * Optimistic: the cached profile flips first so the UI re-renders at once;
 * a failed write refetches and reverts.
 */
export function useProfileLocaleMutation() {
  const session = useSession()
  const queryClient = useQueryClient()
  const userId =
    session.status === 'authenticated' || session.status === 'no_subscription'
      ? session.user.id
      : null

  async function save(locale: Locale): Promise<{ error: string | null }> {
    if (!userId) return { error: 'no_profile' }
    const key = sessionKeys.profile(userId)

    const previous = queryClient.getQueryData<ProfileQueryData>(key)
    if (previous) {
      queryClient.setQueryData<ProfileQueryData>(key, {
        ...previous,
        profile: { ...previous.profile, locale },
      })
    }

    const { error } = await supabase.rpc('set_my_locale', { p_product: getProduct().id, p_locale: locale })
    if (error) {
      await queryClient.invalidateQueries({ queryKey: key })
      return { error: error.message }
    }
    return { error: null }
  }

  return { userId, save }
}
